import axios from 'axios';
import config from './config';
import router from '@/router';
import { Message } from '@/utils/resetPopup';

/**
 * 【per-user HttpOnly Cookie 认证方案】
 * - 后端 SysLoginController 写 HttpOnly Cookie: access_token_{username}, refresh_token_{username}
 * - 后端同时写非 HttpOnly Cookie: current_user={username}（前端可读）
 * - 请求全靠浏览器自动携带 Cookie，Stone-wms JwtTokenUtils.getToken() 从 Cookie 读
 * - 前端 sessionStorage 只存 current_user（当前用户名），不再存 token
 * - 退出时 clearTokens() 只清理 sessionStorage，Cookie 由后端 /logout 清理
 */

const USER_KEY = 'user';             // 当前用户名 key

// ── 当前用户名 ───────────────────────────────────
function getCurrentUser() {
  return sessionStorage.getItem(USER_KEY) || '';
}

// ── 清除登录状态（退出登录时调用）───────────────────────
// 注意：token 在 HttpOnly Cookie 中，由后端 /logout 清理
// 这里只清理前端 sessionStorage 中的 user 标识
export function clearTokens() {
  sessionStorage.removeItem(USER_KEY);
}

// ── 刷新 Access Token ─────────────────────────────────
async function refreshAccessToken() {
  try {
    // refresh_token 在 HttpOnly Cookie 中，浏览器自动发送
    // 后端 refresh 后会写新的 per-user Cookie
    const resp = await axios.create({
      baseURL: config.baseUrl || '',
      withCredentials: true
    }).post('/api/token/refresh', {});
    if (resp.data && resp.data.data && resp.data.data.access_token) {
      // token 已写入 HttpOnly Cookie，前端无需再存
      return resp.data.data.access_token;
    }
  } catch (e) {
    console.error('Refresh token failed:', e);
  }
  return null;
}

// ── Axios 实例 ────────────────────────────────────────
export default function $axios(options) {
  return new Promise((resolve, reject) => {
    const instance = axios.create({
      baseURL: config.baseUrl,
      headers: config.headers,
      timeout: config.timeout,
      withCredentials: true  // 允许携带 Cookie（跨域请求带上 Cookie）
    });

    // ── 请求拦截器：不附加 Authorization header────────────
    // token 全部由浏览器自动携带 HttpOnly Cookie，后端 getToken() 从 Cookie 读
    // 每次请求前写 user Cookie（非 HttpOnly），供后端解析用户名
    instance.interceptors.request.use(
      cfg => {
        const currentUser = getCurrentUser();
        if (currentUser) {
          document.cookie = `user=${currentUser}; Path=/; SameSite=Lax`;
        }
        delete cfg.headers['Authorization'];
        delete cfg.headers['token'];
        return cfg;
      },
      error => Promise.reject(error)
    );

    // ── 响应拦截器 ────────────────────────────────────
    instance.interceptors.response.use(
      response => {
        const data = response.data;

        // 登录成功后：从 JWT token 解析 username，存 sessionStorage，并写 user Cookie
        if (data && data.data && data.data.access_token) {
          try {
            const token = data.data.access_token;
            const payload = JSON.parse(atob(token.split('.')[1]));
            const username = payload.sub || null;
            if (username) {
              sessionStorage.setItem(USER_KEY, username);
              // 写非 HttpOnly user Cookie，供后端读取（多用户不覆盖）
              document.cookie = `user=${username}; Path=/; SameSite=Lax`;
            }
          } catch (e) { /* ignore */ }
        }

        switch (data && data.code) {
          case -10000:
            Message({ message: data.msg, type: 'error', duration: 2000 });
            break;
          default:
        }
        return data;
      },
      async error => {
        const origRequest = error.config;

        // 401：尝试刷新 token
        if (error.response && error.response.status === 401 && !origRequest._retry) {
          origRequest._retry = true;

          const newToken = await refreshAccessToken();
          if (newToken) {
            // token 已写入 Cookie，重试请求会自动携带
            return instance(origRequest);
          } else {
            clearTokens();
            router.push('/login');
            return Promise.reject(error);
          }
        }

        // 其他错误码
        if (error.response) {
          switch (error.response.status) {
            case 400:  error.message = '请求错误'; break;
            case 401:  error.message = '未授权，请登录'; break;
            case 403:  error.message = '拒绝访问'; break;
            case 404:  error.message = '请求地址出错'; break;
            case 405:  error.message = '您的账号已在其他设备登录'; break;
            case 408:  error.message = '请求超时'; break;
            case 500:  error.message = '服务器内部错误'; break;
            case 502:  error.message = '网关错误'; break;
            case 503:  error.message = '服务不可用'; break;
            case 504:  error.message = '网关超时'; break;
          }
          if (error.response.status === 401 || error.response.status === 405) {
            clearTokens();
            router.push('/login');
          }
        }
        console.error(error);
        return Promise.reject(error);
      }
    );

    instance(options).then(res => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}


// WEBPACK FOOTER //
// ./src/http/axios.js